import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { Loader2, Link as LinkIcon } from 'lucide-react';
import { extractVideoMetadata } from '@/lib/videoMetadata';

interface AddBookmarkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddBookmarkDialog({ open, onOpenChange }: AddBookmarkDialogProps) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  
  const { mutate: publishEvent, isPending } = useNostrPublish();
  const { toast } = useToast();

  const handleUrlBlur = async () => {
    if (!url || title) return;
    
    setIsFetching(true);
    try {
      const metadata = await extractVideoMetadata(url);
      if (metadata.title) setTitle(metadata.title);
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
    } finally {
      setIsFetching(false);
    }
  };

  const handleSubmit = () => {
    const tagList = tags.split(',').map(t => t.trim()).filter(Boolean);
    
    const tagsArray = [
      ['title', title],
      ['imeta', `url ${url}`],
    ];
    
    if (description) {
      tagsArray.push(['description', description]);
    }
    
    tagList.forEach(tag => {
      tagsArray.push(['t', tag]);
    });

    publishEvent(
      {
        kind: 21,
        content: description,
        tags: tagsArray,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Bookmark saved!',
            description: 'Your video has been bookmarked on Nostr.',
          });
          onOpenChange(false);
          setUrl('');
          setTitle('');
          setDescription('');
          setTags('');
        },
        onError: (error) => {
          toast({
            title: 'Failed to bookmark',
            description: error.message || 'Please try again.',
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center space-x-2">
              <LinkIcon className="h-5 w-5 text-pink-500" />
              <span>Add Video Bookmark</span>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="url">Video URL</Label>
            <Input
              id="url"
              type="url"
              placeholder="https://www.youtube.com/watch?v=..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onBlur={handleUrlBlur}
              disabled={isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              placeholder="Video title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isPending || isFetching}
            />
            {isFetching && (
              <p className="text-xs text-muted-foreground">Fetching title from URL...</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Notes (optional)</Label>
            <Textarea
              id="description"
              placeholder="Your notes about this video..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              disabled={isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tags">Tags (comma-separated)</Label>
            <Input
              id="tags"
              placeholder="documentary, space, science..."
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              disabled={isPending}
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={!url || !title || isPending}
            className="w-full bg-pink-500 hover:bg-pink-600"
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Bookmark'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}